import type { ChangeAllLegChange, OrderLeg } from '../services/orders.service';
import { formatBackendDate } from './dateUtils';

// Convert a date-ish value to YYYY-MM-DD, returning '' for empty input. This is
// the SAME normalization legSavePayload's `fmtDate` applies to leg appt dates,
// so a bulk "apply to all" value matches what a single save would write.
const fmtDate = (d: unknown): string => {
  if (d === null || d === undefined) return '';
  return formatBackendDate(String(d)) || '';
};

// Eligible whole-leg fields for a "Change All" fan-out: editable leg data
// fields that make sense to copy across a batch of pros. The first element is
// the camelCase key as it appears in `editedLegs[legrecno]` / on `OrderLeg`;
// the second is the v1 leg column the backend writes.
//
// Deliberately EXCLUDED: driver, any arrival/depart datetimes, sendToGwiMove,
// and the role keys themselves (legtype / legAction2) — those either don't
// bulk-copy safely or are how the target leg is resolved, not a written value.
//
// NOTE the memo keys are lowercase (`origmemo` / `legmemo`) — the real OrderLeg
// field names — not the `origMemo` / `legMemo` shapes; `legDrop` stays
// camelCase because that is its actual OrderLeg field name.
export const LEG_BULK_FIELD_COLUMNS: ReadonlyArray<readonly [string, string]> = [
  ['origcode', 'origcode'],
  ['destcode', 'destcode'],
  ['origApptDate', 'origapptdate'],
  ['destApptDate', 'destapptdate'],
  ['origApptTime1', 'origappttime1'],
  ['origApptTime2', 'origappttime2'],
  ['destApptTime1', 'destappttime1'],
  ['destApptTime2', 'destappttime2'],
  ['origApptNumber', 'origapptnumber'],
  ['destApptNumber', 'destapptnumber'],
  ['origmemo', 'origmemo'],
  ['legmemo', 'legmemo'],
  ['legDrop', 'legdrop'],
];

// v1 leg columns that must be normalized to YYYY-MM-DD on the wire (the two
// appointment dates legSavePayload runs through `fmtDate`). All other columns
// pass through unchanged.
const LEG_DATE_COLUMNS: ReadonlySet<string> = new Set(['origapptdate', 'destapptdate']);

// Resolve the leg-fan-out `match` descriptor for a single leg. Mirrors the
// order-level leg-pointer rules in legSavePayload:
//   originate = legtype 'PICKUP', delivery = legAction2 'DELIVERY',
//   terminate = legAction2 'TERMINATE'.
// Role wins over legnum; a leg with no matching role falls back to its legnum.
const resolveMatch = (leg: OrderLeg): ChangeAllLegChange['match'] => {
  if ((leg.legtype ?? '') === 'PICKUP') return { role: 'originate' };
  if ((leg.legAction2 ?? '') === 'DELIVERY') return { role: 'delivery' };
  if ((leg.legAction2 ?? '') === 'TERMINATE') return { role: 'terminate' };
  return { legnum: leg.legnum };
};

/**
 * Change-All variant for legs: for each leg the user checked "apply to all",
 * emit one `{ match, column, newValue }` per eligible edited field, so the
 * backend can resolve the corresponding leg on every target pro (by role, else
 * by legnum) and write the value.
 *
 * - `checkedLegs` holds legrecno values as STRINGS.
 * - `newValue` uses the same date normalization as a single leg save.
 * - Returns a flat array (empty when nothing checked / nothing eligible edited).
 */
export function buildLegChangeAllRows(
  editedLegs: Record<number, Partial<OrderLeg>>,
  effectiveLegs: OrderLeg[],
  checkedLegs: ReadonlySet<string>,
): ChangeAllLegChange[] {
  const rows: ChangeAllLegChange[] = [];

  for (const legKey of checkedLegs) {
    const leg = effectiveLegs.find(l => String(l.legrecno) === legKey);
    if (!leg) continue;

    const edits = editedLegs[leg.legrecno];
    if (!edits) continue;

    const match = resolveMatch(leg);

    for (const [field, column] of LEG_BULK_FIELD_COLUMNS) {
      const value = (edits as Record<string, unknown>)[field];
      if (value === undefined) continue;
      const newValue = LEG_DATE_COLUMNS.has(column) ? fmtDate(value) : value;
      rows.push({ match, column, newValue });
    }
  }

  return rows;
}
